import * as React from 'react';

import { getDynamicData, useCurrentCountry, useCurrentLanguage, useDynamicData, useObservable } from '@avensia/excite';
import { styled } from '@glitz/react';
import { useCustomerType } from '@avensia/excite-commercetools';
import { acceptedCookies } from 'webapp/shared/cookie-state';
import { useKlarnaOnSiteMessaging } from './KlarnaOnSiteMessaging/use-klarna-on-site-messaging';
import { KlarnaPlacement, KlarnaPlacementLayout } from './KlarnaOnSiteMessaging/klarna-placement';

type PropType = {
  price: NordicNest.Api.PriceApiModel;
};

const KlarnaOnSiteMessagingWrapper = ({ price }: PropType) => {
  const currentLanguage = useCurrentLanguage();
  const currentCountry = useCurrentCountry();
  const currentLocale = currentLanguage.substring(0, 2) + '-' + currentCountry.twoLetterCode;
  const hasAcceptedCookies = useObservable(acceptedCookies);
  const { isCompanyCustomer } = useCustomerType();
  const [displayKlarnaOnSiteMessaging, setDisplayKlarnaOnSiteMessaging] = React.useState(false);

  const klarnaSettingsProvider = React.useCallback(async () => {
    const scriptSettings = await getDynamicData<NordicNest.Api.ScriptSettingsApiModel>().current();
    return {
      dataClientId: scriptSettings.klarnaOnSiteMessagingClientDataId,
      scriptUrl: scriptSettings.klarnaOnSiteMessagingScriptUrl,
    };
  }, []);

  const klarnaScriptRef = useKlarnaOnSiteMessaging(klarnaSettingsProvider);

  const [scriptSettings] = useDynamicData<NordicNest.Api.ScriptSettingsApiModel>();
  React.useEffect(() => {
    if (!hasAcceptedCookies && isCompanyCustomer) {
      return;
    }

    if (window.CookieInformation && !window.CookieInformation.getConsentGivenFor('cookie_cat_functional')) {
      return;
    }

    let amountLimit: number | undefined;
    if (scriptSettings?.klarnaOnSiteMessagingAmountLimits) {
      const localSettings = JSON.parse(scriptSettings.klarnaOnSiteMessagingAmountLimits) as Record<string, number>;
      if (localSettings !== null && currentCountry.twoLetterCode in localSettings) {
        amountLimit = localSettings[currentCountry.twoLetterCode];
      }
    }

    if (amountLimit === undefined) return;

    const display = price.current.inclVat >= amountLimit;
    setDisplayKlarnaOnSiteMessaging(display);
  }, [
    hasAcceptedCookies,
    isCompanyCustomer,
    scriptSettings?.klarnaOnSiteMessagingAmountLimits,
    price,
    currentCountry.twoLetterCode,
  ]);

  React.useEffect(() => {
    if (window.KlarnaOnsiteService) {
      window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
    }
  }, [displayKlarnaOnSiteMessaging]);

  return (
    <KlarnaMessagesContainer ref={klarnaScriptRef}>
      {displayKlarnaOnSiteMessaging ? (
        <KlarnaPlacement
          layout={KlarnaPlacementLayout.CreditPromotionAutoSize}
          dataLocale={currentLocale}
          dataPurchaseAmountCents={price.current.inclVat * 100}
        />
      ) : null}
    </KlarnaMessagesContainer>
  );
};

export default KlarnaOnSiteMessagingWrapper;

const KlarnaMessagesContainer = styled.div({
  margin: { top: t => t.sharedSpacing.xs, bottom: t => t.sharedSpacing.xl },
});
